import React, { useEffect, useState, useMemo } from "react";
import { fmtBytes, fmtNum, ago } from "../utils/formatters";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import {
  DonutChart,
  BarChart,
  MiniKPI,
} from "./DiagCharts";

// ── Device row ───────────────────────────────────────────────────────────────

function DeviceCard({ device, expanded, onToggle }) {
  const appEntries = useMemo(() => {
    if (!device.app_breakdown) return [];
    return Object.entries(device.app_breakdown).sort(([, a], [, b]) => b - a);
  }, [device.app_breakdown]);

  const topApp = appEntries.length > 0 ? appEntries[0] : null;

  // Donut segments from app breakdown
  const appSegments = useMemo(() => {
    return appEntries.map(([app, count]) => ({ label: app, value: count }));
  }, [appEntries]);

  return (
    <div className={"diag-device-card " + (expanded ? "expanded" : "")}>
      <div className="diag-device-header" onClick={onToggle}>
        <div className="diag-device-primary">
          <span className="diag-device-ip">{device.ip}</span>
          {device.hostname && <span className="diag-device-hostname">{device.hostname}</span>}
          {device.mac && <span className="diag-device-mac">{device.mac}</span>}
        </div>
        <div className="diag-device-summary">
          <span className="diag-device-stat">
            <small>TX</small> {fmtBytes(device.tx_bytes)}
          </span>
          <span className="diag-device-stat">
            <small>RX</small> {fmtBytes(device.rx_bytes)}
          </span>
          {topApp && <span className="app-badge">{topApp[0]}</span>}
          <span className="diag-device-last-seen">{ago(device.last_seen)}</span>
          <span className={"diag-expand-chevron " + (expanded ? "open" : "")}>{"\u25B8"}</span>
        </div>
      </div>

      {expanded && (
        <div className="diag-device-detail">
          {/* KPI row */}
          <div className="diag-kpi-strip">
            <MiniKPI label="TX Packets" value={fmtNum(device.tx_packets)} color="#6366f1" />
            <MiniKPI label="RX Packets" value={fmtNum(device.rx_packets)} color="#06b6d4" />
            <MiniKPI label="TX Data" value={fmtBytes(device.tx_bytes)} color="#8b5cf6" />
            <MiniKPI label="RX Data" value={fmtBytes(device.rx_bytes)} color="#14b8a6" />
            <MiniKPI label="First Seen" value={ago(device.first_seen)} />
            <MiniKPI label="Last Seen" value={ago(device.last_seen)} />
          </div>

          {/* App breakdown: donut + bar */}
          {appEntries.length > 0 && (
            <div className="diag-card" style={{ marginTop: 12 }}>
              <div className="diag-card-title">Application Breakdown</div>
              <div className="diag-device-donut-row">
                <DonutChart
                  segments={appSegments}
                  size={130}
                  thickness={20}
                  centerLabel="Apps"
                  centerValue={String(appEntries.length)}
                  showLegend={false}
                />
                <div style={{ flex: 1 }}>
                  <BarChart
                    items={appEntries.slice(0, 8).map(([app, count]) => ({
                      label: app,
                      value: count,
                    }))}
                    height={18}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

export default function DiagDevicesPanel({ nodeApiUrl, selectedNode }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [expandedIps, setExpandedIps] = useState(new Set());
  const [sortBy, setSortBy] = useState("traffic");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedNode) { setDevices([]); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);

    const poll = async () => {
      try {
        const url = nodeApiUrl("/diagnostics/devices");
        if (!url) return;
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) throw new Error("" + res.status);
        const data = await res.json();
        if (!controller.signal.aborted) {
          setDevices(Array.isArray(data?.devices) ? data.devices : []);
          setLastFetch(new Date());
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("[DiagDevices]", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]);

  const toggleExpand = (ip) => {
    setExpandedIps((prev) => {
      const next = new Set(prev);
      if (next.has(ip)) next.delete(ip); else next.add(ip);
      return next;
    });
  };

  const sorted = useMemo(() => {
    let list = [...devices];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (d) => (d.ip || "").toLowerCase().includes(q) ||
               (d.mac || "").toLowerCase().includes(q) ||
               (d.hostname || "").toLowerCase().includes(q)
      );
    }
    if (sortBy === "traffic") {
      list.sort((a, b) => ((b.tx_bytes || 0) + (b.rx_bytes || 0)) - ((a.tx_bytes || 0) + (a.rx_bytes || 0)));
    } else if (sortBy === "last_seen") {
      list.sort((a, b) => new Date(b.last_seen) - new Date(a.last_seen));
    } else if (sortBy === "ip") {
      list.sort((a, b) => (a.ip || "").localeCompare(b.ip || ""));
    } else if (sortBy === "app") {
      const topApp = (d) => {
        if (!d.app_breakdown) return "";
        const entries = Object.entries(d.app_breakdown);
        if (entries.length === 0) return "";
        entries.sort(([, a], [, b]) => b - a);
        return entries[0][0];
      };
      list.sort((a, b) => topApp(a).localeCompare(topApp(b)));
    }
    return list;
  }, [devices, sortBy, search]);

  const totalTraffic = useMemo(() => {
    let tx = 0, rx = 0;
    for (const d of devices) { tx += d.tx_bytes || 0; rx += d.rx_bytes || 0; }
    return { tx, rx };
  }, [devices]);

  // Global app aggregate for summary donut
  const globalApps = useMemo(() => {
    const map = {};
    for (const d of devices) {
      if (!d.app_breakdown) continue;
      for (const [app, cnt] of Object.entries(d.app_breakdown)) {
        map[app] = (map[app] || 0) + cnt;
      }
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .map(([app, count]) => ({ label: app, value: count }));
  }, [devices]);

  if (!selectedNode) {
    return (
      <div className="panel diag-panel">
        <div className="panel-title">Devices &amp; Flows</div>
        <div className="panel-sub">Select an edge node to see connected devices.</div>
      </div>
    );
  }

  return (
    <div className="panel diag-panel">
      <div className="panel-title">Devices &amp; Flows {"\u2014 "}{selectedNode}</div>
      <div className="panel-sub">
        {loading && devices.length === 0
          ? "Loading device data\u2026"
          : devices.length + " devices detected \u00B7 TX " + fmtBytes(totalTraffic.tx) + " \u00B7 RX " + fmtBytes(totalTraffic.rx)}
        <span className="diag-refresh-ts">
          {lastFetch ? " Updated " + lastFetch.toLocaleTimeString() : "\u00A0"}
        </span>
      </div>

      {/* Summary cards */}
      {devices.length > 0 && (
        <div className="diag-row cols-2" style={{ marginBottom: 16 }}>
          <div className="diag-card">
            <div className="diag-card-title">Traffic Summary</div>
            <div className="diag-kpi-strip">
              <MiniKPI label="Devices" value={devices.length} color="#6366f1" />
              <MiniKPI label="TX Total" value={fmtBytes(totalTraffic.tx)} color="#8b5cf6" />
              <MiniKPI label="RX Total" value={fmtBytes(totalTraffic.rx)} color="#06b6d4" />
            </div>
          </div>
          {globalApps.length > 0 && (
            <div className="diag-card">
              <div className="diag-card-title">Application Mix (All Devices)</div>
              <DonutChart
                segments={globalApps.slice(0, 8)}
                size={130}
                thickness={20}
                centerLabel="Apps"
                centerValue={String(globalApps.length)}
                maxLegend={6}
              />
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="diag-devices-toolbar">
        <input
          type="text"
          className="diag-search-input"
          placeholder="Search IP, MAC, hostname\u2026"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="diag-sort-group">
          <span className="diag-sort-label">Sort:</span>
          {["traffic", "last_seen", "ip", "app"].map((s) => (
            <button
              key={s}
              className={"diag-sort-btn " + (sortBy === s ? "active" : "")}
              onClick={() => setSortBy(s)}
            >
              {s === "traffic" ? "Traffic" : s === "last_seen" ? "Recent" : s === "ip" ? "IP" : "App"}
            </button>
          ))}
        </div>
      </div>

      {/* Device list */}
      <div className="diag-devices-list">
        {sorted.map((device) => (
          <DeviceCard
            key={device.ip}
            device={device}
            expanded={expandedIps.has(device.ip)}
            onToggle={() => toggleExpand(device.ip)}
          />
        ))}
        {sorted.length === 0 && !loading && (
          <div className="diag-empty">
            {search ? "No devices match your search." : "No connected devices detected."}
          </div>
        )}
      </div>
    </div>
  );
}
